import { FC } from 'react';
import { Box, Typography, Radio } from '@mui/material';
import DBHeader from 'src/components/Headers/DialogBoxHeader';
import { AddressBoxProps } from 'src/models/DialogBox.interface';
import Scrollbar from 'src/components/StyledComponents/StyledScrollbar';
import {
  ActionIcon,
  ActionRow,
  ActionText,
  AddAddressBox,
  AddressGrid,
  AddressLine,
  ContactInfo,
  ContactName,
  PhoneIcon,
  StyledAddressBox,
  StyledDialogContent,
  StyledIcon,
  StyledTypography,
  TopRow,
  StyledDialogContainer,
} from './AddressBox.styles';
import { CloseIcon, StyledDialog } from '../DialogBox.styles';
import { styled } from '@mui/material';

const StyledDialogBox = styled(StyledDialog)<{ width: string; height: string }>(
  ({ width, height }) => ({
    '& .MuiDialog-paper': {
      width,
      height,
      maxWidth: 'none',
      overflow: 'hidden',
      borderRadius: '16px',
    },
  })
);
export const StyledCloseIcon = styled(CloseIcon)({
  position: 'absolute',
  top: '16px',
  right: '16px',
  fontSize: '24px',
  cursor: 'pointer',
  color: 'var(--instructions)',
  '&:hover': {
    color: 'var(--error)',
  },
});

const AddressBox: FC<AddressBoxProps> = ({
  height = '700px',
  width = '1024px',
  boxHeight = '250px',
  boxWidth = '275px',
  open,
  title,
  subtitle,
  addresses,
  handleClose,
  onAddNewAddressClick,
  handleAddressDelete,
  handleDefaultAddress,
  handleAddressEdit,
}) => {
  return (
    <StyledDialogBox
      open={open}
      onClose={handleClose}
      width={width}
      height={height}
    >
      {/* Replaced inline sx Box with styled component */}
      <StyledDialogContainer>
        <DBHeader title={title} subtitle={subtitle} />
        <StyledCloseIcon
          component="i"
          className="bx bx-x-circle"
          onClick={handleClose}
        />
        <Scrollbar thumbColor="var(--loader-color)">
          <StyledDialogContent>
            <AddressGrid>
              <AddAddressBox
                boxHeight={boxHeight}
                boxWidth={boxWidth}
                onClick={onAddNewAddressClick}
              >
                <StyledTypography>
                  <StyledIcon component="i" className="bx bx-plus-circle" />
                  Add new address
                </StyledTypography>
              </AddAddressBox>

              {addresses
                .sort((a, b) => {
                  if (a.isDefault && !b.isDefault) return -1;
                  if (!a.isDefault && b.isDefault) return 1;
                  return a.contactPersonName.localeCompare(b.contactPersonName);
                })
                .map((address, index) => (
                  <StyledAddressBox
                    key={index}
                    boxHeight={boxHeight}
                    boxWidth={boxWidth}
                  >
                    <TopRow>
                      <Typography
                        fontFamily="Outfit"
                        fontWeight="400"
                        fontSize="12px"
                        color={
                          address.isDefault
                            ? 'var(--primary-blue)'
                            : 'var(--subtext)'
                        }
                      >
                        {address.isDefault ? 'Default' : 'Set as Default'}
                      </Typography>
                      <Radio
                        checked={address.isDefault}
                        onChange={() => handleDefaultAddress(address.id)}
                        value="default"
                        name="radio-buttons"
                        sx={{
                          color: address.isDefault
                            ? 'var(--primary-blue)'
                            : 'inherit',
                          '&.Mui-checked': {
                            color: 'var(--primary-blue)',
                          },
                        }}
                        size="small"
                      />
                    </TopRow>

                    <ContactName>{address.contactPersonName}</ContactName>

                    <AddressLine>{address.addressLine1}</AddressLine>
                    <AddressLine>{address.addressLine2}</AddressLine>
                    <AddressLine>{address.city}</AddressLine>
                    <AddressLine>{address.postCode}</AddressLine>
                    <AddressLine>{address.country}</AddressLine>

                    <ContactInfo>
                      <PhoneIcon component="i" className="bx bx-phone-call" />
                      {address.contactNo}
                    </ContactInfo>

                    <ActionRow>
                      <ActionText onClick={() => handleAddressEdit(address.id)}>
                        <ActionIcon component="i" className="bx bx-edit" />
                        Edit
                      </ActionText>
                      <ActionText
                        onClick={() => handleAddressDelete(address.id)}
                      >
                        <ActionIcon component="i" className="bx bx-trash" />
                        Delete
                      </ActionText>
                    </ActionRow>
                  </StyledAddressBox>
                ))}
            </AddressGrid>
          </StyledDialogContent>
        </Scrollbar>
      </StyledDialogContainer>
    </StyledDialogBox>
  );
};

export default AddressBox;
